import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import Layout from "./Layout";
import BlogRightSidebar from "./BlogRightSidebar";
import {
  ArrowUpRight,
  Clock,
  Search,
  Menu,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { getSortedPosts } from "../data/blogPosts";

export default function Main() {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 10;
  const blogPosts = getSortedPosts();

  // Filter posts based on search query
  const filteredPosts = blogPosts.filter((post) =>
    searchQuery.trim() === ""
      ? true
      : post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const currentPosts = filteredPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage,
  );

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  return (
    <Layout rightSidebar={<BlogRightSidebar />} showFloatingMenu={true}>
      {({ toggleMobileMenu }) => (
        <div className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-12">
          <div className="w-full max-w-content">
            {/* Mobile Menu + Search Bar - Top Row */}
            <div className="flex items-center justify-between mb-8">
              {/* Hamburger Menu - Mobile Only */}
              <button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-lg lg:hidden ${
                  isDark
                    ? "text-gray-400 hover:text-white hover:bg-gray-700"
                    : "text-text-muted hover:text-text hover:bg-gray-100"
                }`}
                aria-label="Toggle menu"
              >
                <Menu size={20} />
              </button>

              {/* Spacer for desktop */}
              <div className="hidden lg:block flex-1"></div>

              {/* Search Bar */}
              <div className="relative" style={{ width: "240px" }}>
                <Search
                  size={14}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                    isDark ? "text-gray-500" : "text-text-muted"
                  }`}
                />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-9 pr-3 py-1.5 rounded text-xs transition-all duration-base ${
                    isDark
                      ? "bg-background-dark text-gray-300 border border-border-dark placeholder-gray-600 focus:border-border-dark focus:outline-none shadow-sm"
                      : "bg-background text-text border border-border/30 placeholder-text-muted/60 focus:border-border focus:outline-none shadow-sm"
                  }`}
                  style={{
                    boxShadow: isDark
                      ? "inset 0 1px 2px rgba(0,0,0,0.3)"
                      : "inset 0 1px 2px rgba(0,0,0,0.05)",
                  }}
                />
              </div>
            </div>

            {/* Page Header */}
            <header className="mb-12">
              <h1
                className={`text-4xl font-display font-semibold mb-3 tracking-tight ${
                  isDark ? "text-white" : "text-text"
                }`}
              >
                Recent Posts
              </h1>
              <p
                className={`text-base leading-normal ${
                  isDark ? "text-gray-400" : "text-text-muted"
                }`}
              >
                Technical writing on systems programming, infrastructure, and
                reliable software
              </p>
            </header>

            {/* Blog Posts */}
            <div className="space-y-10">
              {currentPosts.map((post, index) => (
                <article
                  key={index}
                  className={`group pb-10 border-b last:border-b-0 ${
                    isDark ? "border-border-dark" : "border-border"
                  }`}
                >
                  {/* Tags at top - max 3 */}
                  <div className="flex gap-2 mb-1 -mt-2">
                    {post.tags.slice(0, 3).map((tag, tagIndex) => (
                      <Link
                        key={tagIndex}
                        to={`/tags/${tag.toLowerCase()}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border transition-all duration-base ${
                          isDark
                            ? "bg-background-dark text-gray-400 border-border-dark hover:border-primary-dark/50 hover:bg-border-dark"
                            : "bg-background-sidebar text-text-muted border-border/50 hover:border-primary/50 hover:bg-secondary/20"
                        }`}
                      >
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${isDark ? "bg-primary-dark" : "bg-primary"}`}
                        ></div>
                        {tag}
                      </Link>
                    ))}
                  </div>

                  <h2
                    className={`text-2xl font-display font-medium leading-snug tracking-tight mb-3 -mt-1 ${
                      isDark ? "text-white" : "text-text"
                    }`}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className={`transition-colors duration-base inline-flex items-center gap-2 group ${isDark ? "hover:text-primary-dark" : "hover:text-primary"}`}
                    >
                      {post.title}
                      <ArrowUpRight
                        size={20}
                        className={`opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-base ${isDark ? "text-primary-dark" : ""}`}
                      />
                    </Link>
                  </h2>

                  <p
                    className={`text-base leading-normal mb-5 ${
                      isDark ? "text-gray-400" : "text-text-muted"
                    }`}
                  >
                    {post.excerpt}
                  </p>

                  {/* Date and Read Time - Bottom right */}
                  <div className="flex justify-end">
                    <div
                      className={`flex items-center gap-4 text-sm ${
                        isDark ? "text-gray-400" : "text-text-muted"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <div
                          className={`w-1 h-1 rounded-full ${
                            isDark ? "bg-gray-400" : "bg-text-muted"
                          }`}
                        ></div>
                        <time dateTime={post.date}>
                          {new Date(post.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </time>
                      </div>
                      <span>•</span>
                      <div className="flex items-center gap-1.5">
                        <Clock size={14} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-16">
                <button
                  onClick={() => {
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                    window.scrollTo(0, 0);
                  }}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg transition-colors duration-base ${
                    currentPage === 1
                      ? "opacity-50 cursor-not-allowed"
                      : isDark
                        ? "hover:bg-gray-800 text-gray-400 hover:text-white"
                        : "hover:bg-gray-100 text-text-muted hover:text-text"
                  }`}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={20} />
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => {
                          setCurrentPage(page);
                          window.scrollTo(0, 0);
                        }}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-base ${
                          currentPage === page
                            ? isDark
                              ? "bg-primary-dark text-background-dark"
                              : "bg-primary text-white"
                            : isDark
                              ? "text-gray-400 hover:bg-gray-800 hover:text-white"
                              : "text-text-muted hover:bg-gray-100 hover:text-text"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                </div>

                <button
                  onClick={() => {
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
                    window.scrollTo(0, 0);
                  }}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg transition-colors duration-base ${
                    currentPage === totalPages
                      ? "opacity-50 cursor-not-allowed"
                      : isDark
                        ? "hover:bg-gray-800 text-gray-400 hover:text-white"
                        : "hover:bg-gray-100 text-text-muted hover:text-text"
                  }`}
                  aria-label="Next page"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
